import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, EmptyState } from "@/components/ui";
import { ApprovalCard } from "@/components/approvals";
import type { Approval } from "@shared/types";

export function ApprovalsPage() {
  const { can, user } = useAuth();
  const canViewAll = can("projects.view_all");
  const [scope, setScope] = useState<"mine" | "raised" | "all">("mine");
  const [status, setStatus] = useState("pending");

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["approvals", "page", scope, status],
    queryFn: () => api.get<{ approvals: Approval[] }>(`/approvals?scope=${scope}&status=${status}`),
  });

  const scopes: { id: typeof scope; label: string }[] = [
    { id: "mine", label: "For my decision" },
    { id: "raised", label: "Raised by me" },
    ...(canViewAll ? [{ id: "all" as const, label: "All projects" }] : []),
  ];

  return (
    <>
      <PageHeader title="Approvals" subtitle="Deliverable sign-offs and change requests." />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg bg-surface-2 p-1">
          {scopes.map((s) => (
            <button key={s.id} onClick={() => setScope(s.id)}
              className={`rounded-md px-3 py-1.5 text-sm ${scope === s.id ? "bg-surface font-medium shadow-sm" : "text-ink-soft"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`rounded-lg px-3 py-1.5 text-sm capitalize ${status === s ? "bg-brand-700 text-white" : "text-ink-soft hover:bg-surface-2"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? <PageLoader /> : !data?.approvals.length ? (
        <EmptyState title="Nothing here" hint={scope === "mine" ? "You have no approvals waiting on your decision." : "No matching approvals."} />
      ) : (
        <div className="space-y-3">
          {data.approvals.map((a) => (
            <ApprovalCard key={a.id} a={a} onChanged={refetch}
              canDecide={can("approvals.decide") && (a.assignedToId === user?.id || canViewAll || !!user?.isSuperAdmin)} />
          ))}
        </div>
      )}
    </>
  );
}
